import React, { useState, useEffect, useMemo, useRef } from "react";
import Sidebar from "../components/Sidebar";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/components.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import RbacManager from "../utilities/rbac";
import Swal from "sweetalert2";
import axios from "axios";
import {
    Box,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Button,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { text } from "@fortawesome/fontawesome-svg-core";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const BLOB_UPLOAD_URL = `${API_BASE_URL}/promotional-upload-File`;
const BLOB_DELETE_URL = `${API_BASE_URL}/delete-files`;
const GET_ALL_PROMOTIONAL_IMAGES_URL = `${API_BASE_URL}/get-all-promotional-images`;
const CREATE_PROMOTIONAL_IMAGE_URL = `${API_BASE_URL}/create-promotional-image`;
const UPDATE_PROMOTIONAL_IMAGE_URL = `${API_BASE_URL}/update-promotional-image`;

const createEmptyRow = (serialNo) => ({
    serialNo,
    id: serialNo,
    webimageen: null,
    webimagear: null,
    mobimageen: null,
    mobimagear: null,
    startdate: "",
    enddate: "",
    entity: "",
    category: "",
    subCategory: "",
    categoryOptions: [],
    subCategoryOptions: [],
    isExisting: false,
    isDirty: false,
});

const getStatusFromDates = (startDate, endDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!endDate) return "active";

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    return end < today ? "expired" : "active";
};

const getImagePreview = (image) => {
    if (!image) return null;
    if (typeof image === "string") return image;
    return image.preview || image.url || image.fileUrl || null;
};

const getBlobFileName = (image) => {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.blobFileName || image.fileName || image.name || "";
};

const getExtensionFromFile = (file) => {
    const fileName = file?.name || "";
    const extFromName = fileName.includes(".")
        ? fileName.split(".").pop().toLowerCase()
        : "";

    if (extFromName === "jpeg") return "jpg";
    if (extFromName === "jpg" || extFromName === "png") return extFromName;

    if (file?.type === "image/png") return "png";
    return "jpg";
};

const getSlotFileName = (serialNo, imageField, file) => {
    const extension = getExtensionFromFile(file);
    const fieldMap = {
        webimageen: "weben",
        webimagear: "webar",
        mobimageen: "mobileen",
        mobimagear: "mobilear",
    };

    const suffix = fieldMap[imageField];
    return `promotional_image${serialNo}_${suffix}.${extension}`;
};

const renameFileForSlot = (file, serialNo, imageField) => {
    const newFileName = getSlotFileName(serialNo, imageField, file);
    return new File([file], newFileName, {
        type: file.type,
        lastModified: Date.now(),
    });
};

const getDisplayFileName = (serialNo, imageField, fileData) => {
    if (!fileData) return "";

    const previewName =
        typeof fileData === "string"
            ? fileData
            : fileData.fileName || fileData.name || "";

    const extension = previewName.toLowerCase().endsWith(".png") ? "png" : "jpg";

    const fieldMap = {
        webimageen: "weben",
        webimagear: "webar",
        mobimageen: "mobileen",
        mobimagear: "mobilear",
        webimage: "web",
        mobimage: "mobile",
    };

    const suffix = fieldMap[imageField] || imageField;
    return `promotional_image${serialNo}_${suffix}.${extension}`;
};

function ImageDropZone({
    label,
    fileData,
    imageField,
    serialNo,
    onFileChange,
    onDelete,
    disabled = false,
    height = 56,
}) {
    const [isDragging, setIsDragging] = useState(false);
    const previewUrl = getImagePreview(fileData);
    const inputRef = useRef(null);

    const validateFile = (file) => {
        if (!file) return false;

        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            Swal.fire({
                title: "Invalid file",
                text: "Only JPG, JPEG and PNG files are allowed.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return false;
        }

        return true;
    };

    const handleFileSelect = (file) => {
        if (!validateFile(file)) return;
        onFileChange(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    };

    const handleBrowseChange = (e) => {
        if (disabled) return;
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
        e.target.value = "";
    };

    const handleClick = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                onClick={handleClick}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled) setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                sx={{
                    width: "100%",
                    height,
                    border: isDragging
                        ? "2px dashed var(--logo-deep-green)"
                        : "1.5px dashed #cfd8dc",
                    borderRadius: "6px",
                    px: 1.5,
                    backgroundColor: isDragging ? "#f1f8f6" : "#fafafa",
                    cursor: disabled ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    transition: "all 0.2s ease",
                    overflow: "hidden",
                    position: "relative",
                    "&:hover": {
                        borderColor: disabled ? "#cfd8dc" : "var(--logo-deep-green)",
                        backgroundColor: disabled ? "#fafafa" : "#f7fbfa",
                    },
                    "&:hover .image-delete-overlay": {
                        opacity: previewUrl ? 1 : 0,
                        pointerEvents: previewUrl ? "auto" : "none",
                    },
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    onChange={handleBrowseChange}
                />

                {previewUrl ? (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minWidth: 0,
                                width: "100%",
                            }}
                        >
                            <img
                                src={previewUrl}
                                alt={label}
                                style={{
                                    width: "44px",
                                    height: "44px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    border: "1px solid #ddd",
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#333",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        color: "#666",
                                        lineHeight: 1.2,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "100px",
                                    }}
                                >
                                    {getDisplayFileName(serialNo, imageField, fileData)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            className="image-delete-overlay"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete?.();
                            }}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                pointerEvents: "none",
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    color: "#fff",
                                    backgroundColor: "rgba(255,255,255,0.15)",
                                    "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.25)",
                                    },
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </>
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CloudUploadIcon
                            sx={{ fontSize: 20, color: "var(--logo-deep-green)" }}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#333",
                                    lineHeight: 1.15,
                                }}
                            >
                                {label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "9px",
                                    color: "#777",
                                    lineHeight: 1.1,
                                }}
                            >
                                JPG / PNG
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function PromotionalImageEditor() {
    const { t } = useTranslation();
    const { user, token } = useAuth();

    const [entityOptions, setEntityOptions] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingRowNo, setSavingRowNo] = useState(null);

    const categoryCacheRef = useRef({});
    const subCategoryCacheRef = useRef({});
    const categoryInFlightRef = useRef({});
    const subCategoryInFlightRef = useRef({});

    const rbacMgr = new RbacManager(
        user?.userType === "employee" && user?.roles?.[0] !== "admin"
            ? user?.designation
            : user?.roles?.[0],
        "promotionalImageEditor"
    );

    const normalizeEntityOptions = (data) => {
        if (!Array.isArray(data)) return [];

        return data
            .map((item) => {
                if (typeof item === "string") {
                    return { label: item, value: item };
                }

                return {
                    label:
                        item.label ||
                        item.name ||
                        item.entity ||
                        item.code ||
                        item.value ||
                        "",
                    value:
                        item.value ||
                        item.code ||
                        item.entity ||
                        item.name ||
                        item.label ||
                        "",
                };
            })
            .filter((item) => item.label && item.value);
    };

    const fetchEntityOptions = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/basics-masters?filters={"masterName":"entity"}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch entity options");

            const result = await response.json();

            let rawOptions = [];
            if (result.status === "Ok" && result.data) {
                rawOptions = result.data;
            } else if (Array.isArray(result.data)) {
                rawOptions = result.data;
            } else {
                throw new Error("Unexpected response format for entity options");
            }

            const normalizedOptions = normalizeEntityOptions(rawOptions);
            setEntityOptions(normalizedOptions);
            return normalizedOptions;
        } catch (err) {
            console.error("Error fetching entity options:", err);
            setEntityOptions([]);
            return [];
        }
    };

    const updateRow = (serialNo, field, value) => {
        setRows((prev) =>
            prev.map((row) =>
                row.serialNo === serialNo
                    ? { ...row, [field]: value, isDirty: true }
                    : row
            )
        );
    };

    const setRowCategoryOptions = (serialNo, options) => {
        setRows((prev) =>
            prev.map((row) =>
                row.serialNo === serialNo
                    ? {
                        ...row,
                        categoryOptions: options,
                        category:
                            row.category && options.includes(row.category)
                                ? row.category
                                : row.category,
                    }
                    : row
            )
        );
    };

    const setRowSubCategoryOptions = (serialNo, options) => {
        setRows((prev) =>
            prev.map((row) =>
                row.serialNo === serialNo
                    ? {
                        ...row,
                        subCategoryOptions: options,
                        subCategory:
                            row.subCategory && options.includes(row.subCategory)
                                ? row.subCategory
                                : row.subCategory,
                    }
                    : row
            )
        );
    };

    const fetchCategoriesByEntity = async (selectedEntity) => {
        if (!selectedEntity) return [];

        if (categoryCacheRef.current[selectedEntity]) {
            return categoryCacheRef.current[selectedEntity];
        }

        if (categoryInFlightRef.current[selectedEntity]) {
            return categoryInFlightRef.current[selectedEntity];
        }

        categoryInFlightRef.current[selectedEntity] = (async () => {
            const params = new URLSearchParams({ entity: selectedEntity });
            const response = await fetch(
                `${API_BASE_URL}/product-categories?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch categories");

            const result = await response.json();
            const optionsEn = Array.isArray(result.data)
                ? result.data.map((cat) => cat.categoryCodeEn).filter(Boolean)
                : [];

            categoryCacheRef.current[selectedEntity] = optionsEn;
            delete categoryInFlightRef.current[selectedEntity];
            return optionsEn;
        })();

        try {
            return await categoryInFlightRef.current[selectedEntity];
        } catch (error) {
            delete categoryInFlightRef.current[selectedEntity];
            throw error;
        }
    };

    const fetchSubCategoriesByEntityCategory = async (selectedEntity, selectedCategory) => {
        if (!selectedEntity || !selectedCategory) return [];

        const key = `${selectedEntity}__${selectedCategory}`;

        if (subCategoryCacheRef.current[key]) {
            return subCategoryCacheRef.current[key];
        }

        if (subCategoryInFlightRef.current[key]) {
            return subCategoryInFlightRef.current[key];
        }

        subCategoryInFlightRef.current[key] = (async () => {
            const params = new URLSearchParams({
                entity: selectedEntity,
                categoryCodeEn: selectedCategory,
            });

            const response = await fetch(
                `${API_BASE_URL}/product-subcategories?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch subcategories");

            const result = await response.json();
            const optionsEn = Array.isArray(result.data)
                ? result.data.map((sub) => sub.subCategoryCodeEn).filter(Boolean)
                : [];

            subCategoryCacheRef.current[key] = optionsEn;
            delete subCategoryInFlightRef.current[key];
            return optionsEn;
        })();

        try {
            return await subCategoryInFlightRef.current[key];
        } catch (error) {
            delete subCategoryInFlightRef.current[key];
            throw error;
        }
    };

    const loadCategoriesForRow = async (serialNo, selectedEntity) => {
        if (!selectedEntity) {
            setRows((prev) =>
                prev.map((row) =>
                    row.serialNo === serialNo
                        ? {
                            ...row,
                            categoryOptions: [],
                            subCategoryOptions: [],
                            category: "",
                            subCategory: "",
                        }
                        : row
                )
            );
            return;
        }

        try {
            const options = await fetchCategoriesByEntity(selectedEntity);
            setRowCategoryOptions(serialNo, options);
        } catch (err) {
            console.error("Error fetching categories:", err);
            setRows((prev) =>
                prev.map((row) =>
                    row.serialNo === serialNo
                        ? { ...row, categoryOptions: [], subCategoryOptions: [] }
                        : row
                )
            );
        }
    };

    const loadSubCategoriesForRow = async (serialNo, selectedEntity, selectedCategory) => {
        if (!selectedEntity || !selectedCategory) {
            setRows((prev) =>
                prev.map((row) =>
                    row.serialNo === serialNo
                        ? { ...row, subCategoryOptions: [], subCategory: "" }
                        : row
                )
            );
            return;
        }

        try {
            const options = await fetchSubCategoriesByEntityCategory(
                selectedEntity,
                selectedCategory
            );
            setRowSubCategoryOptions(serialNo, options);
        } catch (err) {
            console.error("Error fetching subcategories:", err);
            setRows((prev) =>
                prev.map((row) =>
                    row.serialNo === serialNo
                        ? { ...row, subCategoryOptions: [] }
                        : row
                )
            );
        }
    };

    const hydrateImageObject = (value) => {
        if (!value) return null;
        if (typeof value === "object") return value;

        return {
            url: value,
            preview: value,
            name: value,
            fileName: value,
            blobFileName: value,
        };
    };

    const hydrateFiveRows = (apiRows = []) => {
        const rowsById = Array.isArray(apiRows)
            ? apiRows.reduce((acc, item) => {
                const numericId = Number(item?.id);
                if (numericId >= 1 && numericId <= 5) {
                    acc[numericId] = item;
                }
                return acc;
            }, {})
            : {};

        const mapped = Array.from({ length: 5 }, (_, index) => {
            const serialNo = index + 1;
            const item = rowsById[serialNo];

            if (!item) {
                return createEmptyRow(serialNo);
            }

            return {
                serialNo,
                id: serialNo,
                webimageen: hydrateImageObject(item.webImageEn || null),
                webimagear: hydrateImageObject(item.webImageAr || null),
                mobimageen: hydrateImageObject(item.mobImageEn || null),
                mobimagear: hydrateImageObject(item.mobImageAr || null),
                startdate: item.startDate ? item.startDate.split("T")[0] : "",
                enddate: item.endDate ? item.endDate.split("T")[0] : "",
                entity: item.entity || "",
                category: item.category || "",
                subCategory: item.subCategory || item.subcategory || "",
                categoryOptions: [],
                subCategoryOptions: [],
                isExisting: true,
                isDirty: false,
            };
        });

        setRows(mapped);
        return mapped;
    };

    const hydrateDependentOptions = async (mappedRows) => {
        const uniqueEntities = [...new Set(mappedRows.map((row) => row.entity).filter(Boolean))];

        await Promise.all(
            uniqueEntities.map(async (entity) => {
                try {
                    await fetchCategoriesByEntity(entity);
                } catch (e) {
                    console.error(`Failed categories for entity ${entity}`, e);
                }
            })
        );

        setRows((prev) =>
            prev.map((row) => ({
                ...row,
                categoryOptions: row.entity
                    ? categoryCacheRef.current[row.entity] || []
                    : [],
            }))
        );

        const uniqueEntityCategoryPairs = [
            ...new Set(
                mappedRows
                    .filter((row) => row.entity && row.category)
                    .map((row) => `${row.entity}__${row.category}`)
            ),
        ];

        await Promise.all(
            uniqueEntityCategoryPairs.map(async (pair) => {
                const [entity, category] = pair.split("__");
                try {
                    await fetchSubCategoriesByEntityCategory(entity, category);
                } catch (e) {
                    console.error(
                        `Failed subcategories for entity ${entity} and category ${category}`,
                        e
                    );
                }
            })
        );

        setRows((prev) =>
            prev.map((row) => ({
                ...row,
                categoryOptions: row.entity
                    ? categoryCacheRef.current[row.entity] || []
                    : [],
                subCategoryOptions:
                    row.entity && row.category
                        ? subCategoryCacheRef.current[`${row.entity}__${row.category}`] || []
                        : [],
            }))
        );
    };

    const fetchPromotionalImages = async () => {
        if (!token || !user) return;

        setLoading(true);
        try {
            const response = await axios.get(GET_ALL_PROMOTIONAL_IMAGES_URL, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const apiRows = response?.data?.details;
            const mappedRows = hydrateFiveRows(Array.isArray(apiRows) ? apiRows : []);
            await hydrateDependentOptions(mappedRows);
        } catch (error) {
            console.error("Error fetching promotional images:", error);
            hydrateFiveRows([]);
            Swal.fire({
                title: t("Error"),
                text:
                    error.response?.data?.message ||
                    t("Failed to fetch promotional image data."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            if (!token || !user) return;
            await fetchEntityOptions();
            await fetchPromotionalImages();
        };

        loadInitialData();
    }, [token, user]);

    const validateSelectedFile = (file) => {
        if (!file) return false;

        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            Swal.fire({
                title: t("Invalid file"),
                text: t("Only JPG, JPEG and PNG files are allowed."),
                icon: "warning",
                confirmButtonText: t("OK"),
            });
            return false;
        }

        return true;
    };

    const uploadSingleImageToBlob = async (file, serialNo, imageField) => {
        if (!validateSelectedFile(file)) throw new Error("Invalid file type");

        const renamedFile = renameFileForSlot(file, serialNo, imageField);
        const formData = new FormData();
        formData.append("file", renamedFile);
        formData.append("fileName", renamedFile.name);
        formData.append("containerType", "promotional");

        const response = await axios.post(BLOB_UPLOAD_URL, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${token}`,
            },
        });

        const uploaded = response?.data?.files;

        if (!response?.data?.success || !uploaded?.url) {
            throw new Error(response?.data?.message || "Upload failed");
        }

        return {
            name: uploaded?.fileName || renamedFile.name,
            fileName: uploaded?.fileName || renamedFile.name,
            blobFileName: uploaded?.fileName || renamedFile.name,
            blobPath: uploaded?.blobPath || "",
            url: uploaded?.url || "",
            preview: uploaded?.url || "",
        };
    };

    const handleImageUpload = async (serialNo, imageField, file) => {
        try {
            setLoading(true);
            const uploadedImage = await uploadSingleImageToBlob(file, serialNo, imageField);
            updateRow(serialNo, imageField, uploadedImage);
        } catch (error) {
            if (error.message === "Invalid file type") {
                setLoading(false);
                return;
            }

            console.error(`Error uploading ${imageField}:`, error);
            Swal.fire({
                title: t("Error"),
                text:
                    error.response?.data?.message ||
                    error.message ||
                    t("Failed to upload image."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setLoading(false);
        }
    };

    const removeImageFromBlob = async (image) => {
        const fileName = getBlobFileName(image);
        if (!fileName) return;

        await axios.post(
            BLOB_DELETE_URL,
            {
                fileName,
                containerType: "promotional",
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );
    };

    const handleImageDelete = async (serialNo, imageField) => {
        const row = rows.find((item) => item.serialNo === serialNo);
        const image = row?.[imageField];

        if (!image) return;

        try {
            setLoading(true);
            await removeImageFromBlob(image);
            updateRow(serialNo, imageField, null);

            Swal.fire({
                title: t("Deleted!"),
                text: t("Image has been removed successfully."),
                icon: "success",
                confirmButtonText: t("OK"),
            });
        } catch (error) {
            console.error("Error deleting image from blob:", error);
            Swal.fire({
                title: t("Error"),
                text:
                    error.response?.data?.message ||
                    error.message ||
                    t("Failed to delete image."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setLoading(false);
        }
    };

    const validateRow = (row) => {
        if (!row.webimageen) {
            Swal.fire(t("Validation Error"), t("Web English image is required."), "warning");
            return false;
        }
        if (!row.webimagear) {
            Swal.fire(t("Validation Error"), t("Web Arabic image is required."), "warning");
            return false;
        }
        if (!row.mobimageen) {
            Swal.fire(t("Validation Error"), t("Mobile English image is required."), "warning");
            return false;
        }
        if (!row.mobimagear) {
            Swal.fire(t("Validation Error"), t("Mobile Arabic image is required."), "warning");
            return false;
        }
        if (!row.startdate) {
            Swal.fire(t("Validation Error"), t("Start date is required."), "warning");
            return false;
        }
        if (!row.enddate) {
            Swal.fire(t("Validation Error"), t("End date is required."), "warning");
            return false;
        }
        if (new Date(row.enddate) < new Date(row.startdate)) {
            Swal.fire(
                t("Validation Error"),
                t("End date cannot be earlier than start date."),
                "warning"
            );
            return false;
        }
        if (!row.entity) {
            Swal.fire(t("Validation Error"), t("Entity is required."), "warning");
            return false;
        }
        if (!row.category) {
            Swal.fire(t("Validation Error"), t("Category is required."), "warning");
            return false;
        }
        if (!row.subCategory) {
            Swal.fire(t("Validation Error"), t("Sub category is required."), "warning");
            return false;
        }

        return true;
    };

    const buildPayload = (row) => ({
        id: row?.id ?? null,
        webImageEn: row ? getImagePreview(row.webimageen) : null,
        webImageAr: row ? getImagePreview(row.webimagear) : null,
        mobImageEn: row ? getImagePreview(row.mobimageen) : null,
        mobImageAr: row ? getImagePreview(row.mobimagear) : null,
        startDate: row?.startdate || null,
        endDate: row?.enddate || null,
        entity: row?.entity || null,
        category: row?.category || null,
        subCategory: row?.subCategory || null,
        status: row ? "Active" : null,
    });

    const buildEmptyUpdatePayload = (row) => ({
        id: row?.id ?? row?.serialNo ?? null,
        webImageEn: null,
        webImageAr: null,
        mobImageEn: null,
        mobImageAr: null,
        startDate: null,
        endDate: null,
        entity: null,
        category: null,
        subCategory: null,
        status: null,
    });

    const handleSaveRow = async (row) => {
        if (!validateRow(row)) return;

        setSavingRowNo(row.serialNo);
        try {
            const payload = buildPayload(row);

            const response = await axios.post(CREATE_PROMOTIONAL_IMAGE_URL, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (
                response?.data?.success === true ||
                response?.data?.status?.toLowerCase() === "ok" ||
                response?.status === 200 ||
                response?.status === 201
            ) {
                Swal.fire({
                    title: t("Success"),
                    text: t("Promotional image saved successfully."),
                    icon: "success",
                    confirmButtonText: t("OK"),
                });
                await fetchPromotionalImages();
            }
        } catch (error) {
            console.error("Error saving row:", error);
            Swal.fire({
                title: t("Error"),
                text:
                    error?.response?.data?.message ||
                    t("Failed to save promotional image."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setSavingRowNo(null);
        }
    };

    const handleUpdateRow = async (row) => {
        if (!validateRow(row)) return;

        setSavingRowNo(row.serialNo);
        try {
            const payload = buildPayload(row);

            const response = await axios.post(
                `${UPDATE_PROMOTIONAL_IMAGE_URL}?id=${row.id}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (
                response?.data?.success === true ||
                response?.data?.status?.toLowerCase() === "ok" ||
                response?.status === 200 ||
                response?.status === 201
            ) {
                Swal.fire({
                    title: t("Success"),
                    text: t("Promotional image updated successfully."),
                    icon: "success",
                    confirmButtonText: t("OK"),
                });
                await fetchPromotionalImages();
            }
        } catch (error) {
            console.error("Error updating row:", error);
            Swal.fire({
                title: t("Error"),
                text:
                    error?.response?.data?.message ||
                    t("Failed to update promotional image."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setSavingRowNo(null);
        }
    };

    const handleDeleteRow = async (row) => {
        try {
            setSavingRowNo(row.serialNo);

            if (row.webimageen) {
                try {
                    await removeImageFromBlob(row.webimageen);
                } catch (e) {
                    console.error("Failed to remove webimageen from blob:", e);
                }
            }

            if (row.webimagear) {
                try {
                    await removeImageFromBlob(row.webimagear);
                } catch (e) {
                    console.error("Failed to remove webimagear from blob:", e);
                }
            }

            if (row.mobimageen) {
                try {
                    await removeImageFromBlob(row.mobimageen);
                } catch (e) {
                    console.error("Failed to remove mobimageen from blob:", e);
                }
            }

            if (row.mobimagear) {
                try {
                    await removeImageFromBlob(row.mobimagear);
                } catch (e) {
                    console.error("Failed to remove mobimagear from blob:", e);
                }
            }

            const payload = buildEmptyUpdatePayload(row);

            const response = await axios.post(
                `${UPDATE_PROMOTIONAL_IMAGE_URL}?id=${row.id}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (
                response?.data?.success === true ||
                response?.data?.status?.toLowerCase() === "ok" ||
                response?.status === 200 ||
                response?.status === 201
            ) {
                Swal.fire({
                    title: t("Success"),
                    text: t("Promotional image cleared successfully."),
                    icon: "success",
                    confirmButtonText: t("OK"),
                });
                await fetchPromotionalImages();
            }
        } catch (error) {
            console.error("Error deleting row:", error);
            Swal.fire({
                title: t("Error"),
                text:
                    error?.response?.data?.message ||
                    t("Failed to clear promotional image."),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setSavingRowNo(null);
        }
    };

    const tableRows = useMemo(() => rows, [rows]);

    if (!user || !token) return null;

    return (
        <Sidebar title={t("Promotional Image Editor")}>
            <div className="orders-content">
                {loading && <LoadingSpinner />}

                <Paper
                    sx={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "1px solid #e0e0e0",
                    }}
                >
                    <TableContainer
                        sx={{
                            overflowX: "auto",    // Firefox
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                        }}
                    >
                        <Table sx={{ minWidth: 1460 }}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f8f9fa", padding: "8px" }}>
                                    <TableCell sx={{ fontWeight: 700 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                        {t("Web Image")}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                        {t("Mobile Image")}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 120 }}>
                                        {t("Dates")}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 300 }}>
                                        {t("Classification")}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 60 }}>
                                        {t("Status")}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, width: 80 }}>
                                        {t("Actions")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {tableRows.map((row) => {
                                    const status = row.enddate
                                        ? new Date(row.enddate) < new Date()
                                            ? "expired"
                                            : "active"
                                        : "active";

                                    return (
                                        <TableRow key={row.serialNo} hover>
                                            <TableCell sx={{ width: 20, verticalAlign: "top" }}>
                                                {row.serialNo}
                                            </TableCell>

                                            <TableCell sx={{ width: 120, verticalAlign: "top" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <ImageDropZone
                                                        label={t("English")}
                                                        fileData={row.webimageen}
                                                        imageField="webimageen"
                                                        serialNo={row.serialNo}
                                                        onFileChange={(file) =>
                                                            handleImageUpload(
                                                                row.serialNo,
                                                                "webimageen",
                                                                file
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            handleImageDelete(
                                                                row.serialNo,
                                                                "webimageen"
                                                            )
                                                        }
                                                        height={40}
                                                    />
                                                    <ImageDropZone
                                                        label={t("Arabic")}
                                                        fileData={row.webimagear}
                                                        imageField="webimagear"
                                                        serialNo={row.serialNo}
                                                        onFileChange={(file) =>
                                                            handleImageUpload(
                                                                row.serialNo,
                                                                "webimagear",
                                                                file
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            handleImageDelete(
                                                                row.serialNo,
                                                                "webimagear"
                                                            )
                                                        }
                                                        height={40}
                                                    />
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ width: 120, verticalAlign: "top" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <ImageDropZone
                                                        label={t("English")}
                                                        fileData={row.mobimageen}
                                                        imageField="mobimageen"
                                                        serialNo={row.serialNo}
                                                        onFileChange={(file) =>
                                                            handleImageUpload(
                                                                row.serialNo,
                                                                "mobimageen",
                                                                file
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            handleImageDelete(
                                                                row.serialNo,
                                                                "mobimageen"
                                                            )
                                                        }
                                                        height={40}
                                                    />
                                                    <ImageDropZone
                                                        label={t("Arabic")}
                                                        fileData={row.mobimagear}
                                                        imageField="mobimagear"
                                                        serialNo={row.serialNo}
                                                        onFileChange={(file) =>
                                                            handleImageUpload(
                                                                row.serialNo,
                                                                "mobimagear",
                                                                file
                                                            )
                                                        }
                                                        onDelete={() =>
                                                            handleImageDelete(
                                                                row.serialNo,
                                                                "mobimagear"
                                                            )
                                                        }
                                                        height={40}
                                                    />
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ width: 120, verticalAlign: "top" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        label={t("Start Date")}
                                                        InputLabelProps={{ shrink: true }}
                                                        value={row.startdate}
                                                        onChange={(e) =>
                                                            updateRow(
                                                                row.serialNo,
                                                                "startdate",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        type="date"
                                                        label={t("End Date")}
                                                        InputLabelProps={{ shrink: true }}
                                                        value={row.enddate}
                                                        onChange={(e) =>
                                                            updateRow(
                                                                row.serialNo,
                                                                "enddate",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ width: 300, verticalAlign: "top" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        size="small"
                                                        label={t("Entity")}
                                                        value={row.entity}
                                                        onChange={async (e) => {
                                                            const selectedEntity = e.target.value;
                                                            setRows((prev) =>
                                                                prev.map((r) =>
                                                                    r.serialNo === row.serialNo
                                                                        ? {
                                                                            ...r,
                                                                            entity: selectedEntity,
                                                                            category: "",
                                                                            subCategory: "",
                                                                            categoryOptions: [],
                                                                            subCategoryOptions: [],
                                                                            isDirty: true,
                                                                        }
                                                                        : r
                                                                )
                                                            );
                                                            await loadCategoriesForRow(
                                                                row.serialNo,
                                                                selectedEntity
                                                            );
                                                        }}
                                                    >
                                                        {entityOptions.map((option) => (
                                                            <MenuItem
                                                                key={option.value}
                                                                value={option.value}
                                                            >
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>

                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: "1fr 1fr",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <TextField
                                                            select
                                                            fullWidth
                                                            size="small"
                                                            label={t("Category")}
                                                            value={row.category}
                                                            onChange={async (e) => {
                                                                const selectedCategory = e.target.value;
                                                                setRows((prev) =>
                                                                    prev.map((r) =>
                                                                        r.serialNo === row.serialNo
                                                                            ? {
                                                                                ...r,
                                                                                category:
                                                                                    selectedCategory,
                                                                                subCategory: "",
                                                                                subCategoryOptions: [],
                                                                                isDirty: true,
                                                                            }
                                                                            : r
                                                                    )
                                                                );
                                                                await loadSubCategoriesForRow(
                                                                    row.serialNo,
                                                                    row.entity,
                                                                    selectedCategory
                                                                );
                                                            }}
                                                        >
                                                            {row.categoryOptions.map((option) => (
                                                                <MenuItem key={option} value={option}>
                                                                    {option}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>

                                                        <TextField
                                                            select
                                                            fullWidth
                                                            size="small"
                                                            label={t("Sub Category")}
                                                            value={row.subCategory}
                                                            onChange={(e) =>
                                                                updateRow(
                                                                    row.serialNo,
                                                                    "subCategory",
                                                                    e.target.value
                                                                )
                                                            }
                                                        >
                                                            {row.subCategoryOptions.map((option) => (
                                                                <MenuItem key={option} value={option}>
                                                                    {option}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ width: 60, verticalAlign: "top" }}>
                                                <Chip
                                                    label={
                                                        status === "expired"
                                                            ? t("Expired")
                                                            : t("Active")
                                                    }
                                                    color={
                                                        status === "expired"
                                                            ? "error"
                                                            : "success"
                                                    }
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            <TableCell sx={{ width: 60, verticalAlign: "top" }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    {!row.isExisting || !row.entity ? (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSaveRow(row);
                                                            }}
                                                            disabled={savingRowNo === row.serialNo}
                                                            sx={{
                                                                width: 70,
                                                                height: 32,
                                                                textTransform: "none",
                                                                fontSize: savingRowNo === row.serialNo ? "0.6rem" : "0.78rem",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                px: 1.5,
                                                                boxShadow: "none",
                                                                backgroundColor: "var(--logo-deep-green)",
                                                                color: "#fff",
                                                                "&:hover": {
                                                                    backgroundColor: "#244b47",
                                                                    boxShadow: "none",
                                                                },
                                                                "&:disabled": {
                                                                    backgroundColor: "#bfc8c7",
                                                                    color: "#ffffff",
                                                                },
                                                            }}
                                                        >
                                                            {savingRowNo === row.serialNo ? "Saving..." : "Save"}
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUpdateRow(row);
                                                            }}
                                                            disabled={savingRowNo === row.serialNo}
                                                            sx={{
                                                                width: 70,
                                                                height: 32,
                                                                textTransform: "none",
                                                                fontSize: savingRowNo === row.serialNo ? "0.6rem" : "0.78rem",
                                                                fontWeight: 600,
                                                                borderRadius: "8px",
                                                                px: 1.5,
                                                                borderColor: "var(--logo-deep-green)",
                                                                color: "var(--logo-deep-green)",
                                                                backgroundColor: "transparent",
                                                                "&:hover": {
                                                                    borderColor: "var(--logo-deep-green)",
                                                                    backgroundColor: "rgba(61, 86, 84, 0.08)",
                                                                },
                                                                "&:disabled": {
                                                                    borderColor: "#cfd8d7",
                                                                    color: "#a6b1b0",
                                                                },
                                                            }}
                                                        >
                                                            {savingRowNo === row.serialNo ? "Updating..." : "Update"}
                                                        </Button>
                                                    )}

                                                    <Tooltip title={t("Delete")} arrow>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteRow(row)}
                                                            disabled={savingRowNo === row.serialNo}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </div>
        </Sidebar>
    );
}

export default PromotionalImageEditor;